/**
 * CustomerProposalGroup - Grupo colapsável de propostas por negócio
 *
 * Usado na aba "Propostas" dos modais de Cliente/Contato quando o modo de
 * visualização é "Agrupada". Cada grupo representa um negócio e expande as
 * propostas vinculadas a ele, reaproveitando integralmente o ProposalListRow.
 *
 * Não contém regra de negócio: apenas apresentação + estado de expansão
 * persistido (useTaskGroupExpandedState).
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.1.0 - Exibe o código do negócio (N.Negócio) no cabeçalho do grupo
 *   RELEASE: 2026-08-12 v1.0.0 - Criação inicial
 */

import React from 'react';
import { Briefcase, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDealCode } from '@/lib/formatters';
import { useTaskGroupExpandedState } from '@/hooks/useTasksListViewPreference';
import {
  ProposalListRow,
  type ProposalListRowProps,
} from '@/components/crm/deals/modal/tabs/proposals/ProposalListRow';
import type { DealProposal, ProposalStatus } from '@/hooks/useDealProposals';

/** Situações consideradas "em aberto" para o resumo do cabeçalho */
const OPEN_STATUSES: ProposalStatus[] = ['draft', 'generated', 'sent', 'viewed', 'accepted'];

/** Formata valores monetários (pt-BR) */
function formatCurrency(value: number | null | undefined, currency = 'BRL'): string {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency });
}

/** Props do grupo: dados do negócio + handlers já montados pela aba */
export interface CustomerProposalGroupProps
  extends Omit<ProposalListRowProps, 'proposal' | 'dealLabel'> {
  /** Identificador do negócio (escopo da persistência de expansão) */
  dealId: string;
  /** Título do negócio exibido no cabeçalho */
  dealTitle: string | null;
  /** Propostas do negócio (já ordenadas pela consulta) */
  proposals: DealProposal[];
  /** Expandido por padrão (primeiro grupo) quando não há preferência salva */
  defaultExpanded?: boolean;
}

export function CustomerProposalGroup({
  dealId,
  dealTitle,
  proposals,
  defaultExpanded = false,
  ...rowProps
}: CustomerProposalGroupProps) {
  // Preferência de expansão persistida por negócio
  const [expanded, setExpanded] = useTaskGroupExpandedState(
    `customer-proposals:${dealId}`,
    defaultExpanded,
  );

  // Resumo do cabeçalho: valor da proposta ativa mais recente (se houver)
  const activeProposal = proposals.find((p) => OPEN_STATUSES.includes(p.status));

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Cabeçalho clicável do negócio */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-left transition-colors',
          'bg-muted/40 hover:bg-muted/60',
        )}
      >
        <ChevronRight
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            expanded && 'rotate-90',
          )}
        />
        <Briefcase className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        {/* N.Negócio — mesmo padrão visual da coluna da aba Negócios */}
        <span className="shrink-0 text-xs font-mono text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
          {formatDealCode(dealId)}
        </span>
        <span className="text-sm font-medium truncate min-w-0 flex-1">
          {dealTitle || 'Negócio sem título'}
        </span>
        <span className="text-xs text-muted-foreground shrink-0">
          {proposals.length} proposta{proposals.length === 1 ? '' : 's'}
          {activeProposal
            ? ` · ${formatCurrency(activeProposal.total, activeProposal.currency)} em aberto`
            : ''}
        </span>
      </button>

      {/* Propostas do negócio (sem rótulo do negócio: redundante aqui) */}
      {expanded && (
        <div className="divide-y border-t">
          {proposals.map((proposal) => (
            <ProposalListRow key={proposal.id} proposal={proposal} {...rowProps} />
          ))}
        </div>
      )}
    </div>
  );
}
